import React, { FC, useEffect, useState } from 'react';

import cn from 'classnames';

import Icon from '@components/Icon';
import { ImgShadow, Src } from '@components/ImgShadow';
import { useIsomorphicLayoutEffect } from '@lib/hooks'
import type { IAirdropBox } from '@type/airdrop';

import { Lottie } from '../../Lottie';

import styles from './index.module.less';

export interface CarouselProps {
  width?: number;
  height?: number;
  currentCardClass?: string;
  items: {
    imgInfo: { width: number; height: number; src: Src };
    animationInfo: { name: IAirdropBox };
  }[];
  onChange?: (params: CarouselProps['items'][number]) => void;
}

interface ImgLottieProps {
  index: number;
  currentIndex: number;
  lottieName: IAirdropBox;
  imgSrc: Src;
  imgHeight: number;
  imgWidth: number;
}

export const ImgLottie: FC<ImgLottieProps> = (props) => {
  const { index, currentIndex, lottieName, imgSrc, imgHeight, imgWidth } =
    props;
  const [showImg, setShowImg] = useState<boolean>(false);
  useEffect(() => {
    if (index !== currentIndex) {
      setShowImg(false);
    }
  }, [index, currentIndex]);

  return index === currentIndex && showImg === false ? (
    <Lottie
      name={lottieName}
      className={styles.lottie}
      onFinish={() => setShowImg(true)}
    />
  ) : (
    <div
      className={cn(styles.img, {
        [styles.bigImg]: index === currentIndex
      })}
    >
      <ImgShadow width={imgWidth} height={imgHeight} src={imgSrc} />
    </div>
  );
};

export const Carousel: FC<CarouselProps> = (props) => {
  const { items, onChange } = props;
  const [currentIndex, setCurrentIndex] = useState(0);

  const backwardHandle = () => {
    if (currentIndex === 0) return;
    setCurrentIndex(currentIndex - 1);
  };

  const forwardHandle = () => {
    if (currentIndex === items.length - 1) return;
    setCurrentIndex(currentIndex + 1);
  };

  useIsomorphicLayoutEffect(() => {
    onChange?.(items[currentIndex])
  }, [currentIndex])
  return (
    <div className={styles.container}>
      {items.map((item, index) => {
        return (
          <div
            className={cn(styles.slider, {
              [styles.bigCar]: index === currentIndex
            })}
            key={index}
            onClick={() => {
              setCurrentIndex(index);
            }}
          >
            <ImgLottie
              index={index}
              currentIndex={currentIndex}
              imgSrc={item.imgInfo.src}
              imgHeight={item.imgInfo.height}
              imgWidth={item.imgInfo.width}
              lottieName={item.animationInfo.name}
            />
          </div>
        );
      })}
      <div
        className={cn(styles.btn, styles.btnLeft, {
          [styles.iconDisabled]: currentIndex === 0
        })}
        onClick={backwardHandle}
      >
        <Icon name="backward" className={styles.icon} />
      </div>
      <div
        className={cn(styles.btn, styles.btnRight, {
          [styles.iconDisabled]: currentIndex === items.length - 1
        })}
        onClick={forwardHandle}
      >
        <Icon name="forward" className={styles.icon} />
      </div>
    </div>
  );
};
